
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { X, Wallet, Mail, UserPlus, LogIn, ChevronRight, ShieldCheck, ShieldAlert } from "lucide-react";
import { Button } from "../ui/Button";
import { ChainIcon } from "../ui/ChainIcons";
import { GhostBrandIcon } from "../ui/PartnerIcons";
import { Role } from "../../types";

export const LogonForm: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { login, register, loginWithWallet, isLoading } = useAuth();
  // Set default method to wallet for a web3-first experience
  const [method, setMethod] = useState<'email' | 'wallet'>('wallet');
  const [mode, setMode] = useState<'login' | 'register'>('login');
  
  // Email state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [selectedRole, setSelectedRole] = useState<Role>('merchant');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'login') {
        await login(email, password);
    } else {
        await register(email, password, name, selectedRole);
    }
    onClose();
  };

  const handleWalletConnect = async (chain: 'SOL' | 'SUI') => {
      // Direct integration with loginWithWallet from AuthContext
      await loginWithWallet(chain === 'SOL' ? 'phantom' : 'suiet');
      onClose();
  };

  // Super Admin is hidden, Admin is added
  const roles: { id: Role, label: string, desc: string, icon: any }[] = [
      { id: 'admin', label: 'Network Admin', desc: 'Protocol oversight & cluster management', icon: ShieldAlert },
      { id: 'merchant', label: 'Merchant', desc: 'Accept payments & manage stores', icon: ShieldCheck },
      { id: 'client', label: 'Individual', desc: 'Personal vault & ghost card', icon: Wallet },
      { id: 'agent', label: 'Developer', desc: 'API access & AI agent tools', icon: ChevronRight }
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-stone-900/40 dark:bg-black/70 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-[#15171C] rounded-sm shadow-2xl w-full max-w-md overflow-hidden border border-stone-200 dark:border-[#2D3142] relative transition-colors duration-300">
        <button onClick={onClose} className="absolute top-4 right-4 text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors z-10">
            <X size={20} />
        </button>

        <div className="p-8 pb-6 text-center">
            <div className="flex flex-col items-center mb-6">
                <div className="w-16 h-16 mb-4">
                    <GhostBrandIcon className="w-full h-full drop-shadow-xl" />
                </div>
                <h1 className="font-serif text-3xl font-bold tracking-[0.2em] text-[#0A0B0F] dark:text-white uppercase">Ghost</h1>
                <p className="text-[10px] text-stone-500 dark:text-stone-400 uppercase tracking-[0.4em] font-black">Commerce OS</p>
            </div>
            <h2 className="font-serif text-2xl text-[#0A0B0F] dark:text-white mb-2 font-bold tracking-tight">
                {mode === 'login' ? 'Network Access' : 'Create Identity'}
            </h2>
            <p className="text-stone-500 dark:text-stone-400 text-xs font-bold uppercase tracking-widest opacity-60">
                {mode === 'login' ? 'Connect your vault to continue' : 'Join the sovereign economy'}
            </p>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-200 dark:border-white/10 px-8">
            <button 
                onClick={() => setMethod('wallet')}
                className={`flex-1 pb-3 text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${method === 'wallet' ? 'text-nobel-gold border-b-2 border-nobel-gold' : 'text-stone-500 hover:text-stone-900 dark:text-stone-400 dark:hover:text-white'}`}
            >
                <div className="flex items-center justify-center gap-2"><Wallet size={14}/> Wallet</div>
            </button>
            <button 
                onClick={() => setMethod('email')}
                className={`flex-1 pb-3 text-[10px] font-black uppercase tracking-[0.2em] transition-colors ${method === 'email' ? 'text-nobel-gold border-b-2 border-nobel-gold' : 'text-stone-500 hover:text-stone-900 dark:text-stone-400 dark:hover:text-white'}`}
            >
                <div className="flex items-center justify-center gap-2"><Mail size={14}/> Email</div>
            </button>
        </div>
        
        <div className="p-8 pt-6">
            {method === 'wallet' ? (
                <div className="space-y-4">
                    <button onClick={() => handleWalletConnect('SOL')} className="w-full flex items-center justify-between p-5 rounded-sm border border-stone-100 dark:border-white/5 hover:border-nobel-gold/50 bg-stone-50 dark:bg-white/5 transition-all group shadow-sm">
                        <div className="flex items-center gap-4">
                            <ChainIcon chain="SOL" className="w-8 h-8 grayscale group-hover:grayscale-0 transition-all"/>
                            <div className="text-left">
                                <span className="block font-black text-[10px] text-stone-700 dark:text-white uppercase tracking-widest leading-none mb-1">Solana Mainnet</span>
                                <span className="text-[9px] text-stone-400 font-black uppercase tracking-widest">Via Ghost-Phantom</span>
                            </div>
                        </div>
                        <ChevronRight size={16} className="text-stone-300 group-hover:text-nobel-gold transition-colors" />
                    </button>
                    <button onClick={() => handleWalletConnect('SUI')} className="w-full flex items-center justify-between p-5 rounded-sm border border-stone-100 dark:border-white/5 hover:border-nobel-gold/50 bg-stone-50 dark:bg-white/5 transition-all group shadow-sm">
                        <div className="flex items-center gap-4">
                            <ChainIcon chain="SUI" className="w-8 h-8 grayscale group-hover:grayscale-0 transition-all"/>
                            <div className="text-left">
                                <span className="block font-black text-[10px] text-stone-700 dark:text-white uppercase tracking-widest leading-none mb-1">Sui Network</span>
                                <span className="text-[9px] text-stone-400 font-black uppercase tracking-widest">Via Ghost-Suiet</span>
                            </div>
                        </div>
                        <ChevronRight size={16} className="text-stone-300 group-hover:text-nobel-gold transition-colors" />
                    </button>
                    <div className="pt-6 text-center border-t border-stone-100 dark:border-white/5 mt-2">
                        <div className="flex items-center justify-center gap-2 text-[10px] text-emerald-500 font-black uppercase tracking-[0.2em]">
                            <ShieldCheck size={14} /> End-to-End Encrypted
                        </div>
                    </div>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                    {mode === 'register' && (
                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase tracking-[0.2em] text-stone-500 dark:text-stone-400 ml-1">Identity Name</label>
                            <input
                                type="text"
                                required
                                className="w-full px-4 py-3 rounded-sm bg-stone-50 dark:bg-white/5 border border-stone-100 dark:border-white/10 focus:border-nobel-gold outline-none transition-all text-[#0A0B0F] dark:text-white text-sm font-bold shadow-inner"
                                placeholder="ALEXANDER GHOST"
                                value={name}
                                onChange={(e) => setName(e.target.value.toUpperCase())}
                            />
                        </div>
                    )}
                    
                    <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-[0.2em] text-stone-500 dark:text-stone-400 ml-1">Relay Endpoint</label>
                        <input
                            type="email"
                            required
                            className="w-full px-4 py-3 rounded-sm bg-stone-50 dark:bg-white/5 border border-stone-100 dark:border-white/10 focus:border-nobel-gold outline-none transition-all text-[#0A0B0F] dark:text-white text-sm font-bold shadow-inner"
                            placeholder="OPS@GHOSTPAY.FUN"
                            value={email}
                            onChange={(e) => setEmail(e.target.value.toUpperCase())}
                        />
                    </div>
                    
                    <div className="space-y-1.5">
                        <div className="flex justify-between items-center px-1">
                            <label className="text-[9px] font-black uppercase tracking-[0.2em] text-stone-500 dark:text-stone-400">Vault Passphrase</label>
                            {mode === 'login' && <a href="#" className="text-[9px] font-black text-nobel-gold hover:underline uppercase tracking-widest">Restore?</a>}
                        </div>
                        <input
                            type="password"
                            required
                            className="w-full px-4 py-3 rounded-sm bg-stone-50 dark:bg-white/5 border border-stone-100 dark:border-white/10 focus:border-nobel-gold outline-none transition-all text-[#0A0B0F] dark:text-white text-sm font-bold shadow-inner"
                            placeholder="••••••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>

                    {mode === 'register' && (
                        <div className="space-y-3 pt-2">
                             <label className="text-[9px] font-black uppercase tracking-[0.2em] text-stone-500 dark:text-stone-400 ml-1">Permissions Tier</label>
                             <div className="grid grid-cols-1 gap-2">
                                {roles.map(role => (
                                    <button
                                        key={role.id}
                                        type="button"
                                        onClick={() => setSelectedRole(role.id)}
                                        className={`flex items-center justify-between p-3.5 rounded-sm border text-left transition-all ${selectedRole === role.id ? 'bg-nobel-gold/5 border-nobel-gold shadow-md' : 'bg-white dark:bg-white/5 border-stone-100 dark:border-white/5 hover:border-stone-200'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <role.icon size={16} className={selectedRole === role.id ? 'text-nobel-gold' : 'text-stone-400'} />
                                            <div>
                                                <div className={`text-[10px] font-black uppercase tracking-widest ${selectedRole === role.id ? 'text-nobel-gold' : 'text-[#0A0B0F] dark:text-white'}`}>{role.label}</div>
                                                <div className="text-[8px] text-stone-500 font-bold uppercase tracking-tighter opacity-60">{role.desc}</div>
                                            </div>
                                        </div>
                                        {selectedRole === role.id && <div className="w-1.5 h-1.5 rounded-full bg-nobel-gold shadow-glow"></div>}
                                    </button>
                                ))}
                             </div>
                        </div>
                    )}

                    <div className="pt-4">
                        <Button type="submit" isLoading={isLoading} className="w-full py-4 text-[10px] font-black uppercase tracking-[0.2em] rounded-sm shadow-xl" variant="secondary">
                            {mode === 'login' ? 'Authorize Session' : 'Commit to Ledger'}
                        </Button>
                    </div>

                    <div className="text-center">
                        <button 
                            type="button" 
                            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                            className="text-[10px] font-black text-stone-400 hover:text-nobel-gold transition-colors flex items-center justify-center gap-2 mx-auto group uppercase tracking-[0.1em]"
                        >
                            {mode === 'login' ? <><UserPlus size={12}/> New Identity?</> : <><LogIn size={12}/> Existing Vault?</>}
                            <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                        </button>
                    </div>
                </form>
            )}
        </div>
      </div>
    </div>
  );
};
